const branchKey = (projectPath, branch) =>
	projectPath && branch ? `${projectPath}:${branch}` : null;

const sourceProjectPath = (mr) =>
	mr.sourceProject?.fullPath || mr.project?.fullPath || mr.projectPath || "";

const targetProjectPath = (mr) =>
	mr.targetProject?.fullPath || mr.project?.fullPath || mr.projectPath || "";

const sourceBranchKey = (mr) =>
	branchKey(sourceProjectPath(mr), mr.sourceBranch);
const targetBranchKey = (mr) =>
	branchKey(targetProjectPath(mr), mr.targetBranch);
const LONG_LIVED_BRANCH_NAMES = new Set([
	"main",
	"master",
	"develop",
	"development",
	"trunk",
	"release",
]);

const isLongLivedBranch = (branch) => {
	const normalized = branch?.toLowerCase();
	return (
		LONG_LIVED_BRANCH_NAMES.has(normalized) ||
		normalized?.startsWith("release/")
	);
};

const explicitBlockerIds = (mr) =>
	(mr.blockingMergeRequests || []).map((blocker) => blocker.id);

const canBeStackParent = (mr) =>
	!mr.sourceBranchProtected && !isLongLivedBranch(mr.sourceBranch);

const byCreatedThenIid = (a, b) => {
	const aCreated = new Date(a.createdAt || 0).getTime();
	const bCreated = new Date(b.createdAt || 0).getTime();
	if (aCreated !== bCreated) return aCreated - bCreated;
	return String(a.iid || "").localeCompare(String(b.iid || ""));
};

const summarize = (mr, depth = 0) => ({
	id: mr.id,
	iid: mr.iid,
	title: mr.title,
	webUrl: mr.webUrl,
	projectPath: mr.projectPath || mr.project?.fullPath,
	depth,
	// Enough status for the chain view to show where a stack is held up.
	external: Boolean(mr.external),
	draft: Boolean(mr.draft),
	pipelineStatus: mr.headPipeline?.status ?? null,
	approved: mr.approvalsLeft === 0,
});

const longestDescendantPath = (id, childrenById, mrById) => {
	const childIds = childrenById.get(id) || [];
	if (childIds.length === 0) return [];

	let longestPath = [];
	for (const childId of childIds) {
		const path = [
			childId,
			...longestDescendantPath(childId, childrenById, mrById),
		];
		if (
			path.length > longestPath.length ||
			(path.length === longestPath.length &&
				byCreatedThenIid(mrById.get(path[0]), mrById.get(longestPath[0])) < 0)
		) {
			longestPath = path;
		}
	}

	return longestPath;
};

const emptyStack = Object.freeze({
	isStacked: false,
	stackId: null,
	stackSize: 1,
	stackPosition: 1,
	stackDepth: 0,
	isStackStart: false,
	isBlockedByStack: false,
	blockedBy: null,
	blocks: [],
	alsoBlockedBy: [],
	hiddenBlockers: 0,
	reviewStart: null,
	members: [],
});

const findCycleIds = (ids, parentById) => {
	const cycleIds = new Set();

	for (const id of ids) {
		const seen = new Map();
		let currentId = id;

		while (parentById.has(currentId)) {
			if (seen.has(currentId)) {
				const cycleStart = seen.get(currentId);
				for (const [seenId, index] of seen.entries()) {
					if (index >= cycleStart) cycleIds.add(seenId);
				}
				break;
			}

			seen.set(currentId, seen.size);
			currentId = parentById.get(currentId);
		}
	}

	return cycleIds;
};

export const annotateStackedMergeRequests = (mrs) => {
	if (!mrs?.length) return [];

	const annotated = mrs.map((mr) => ({ ...mr, stack: emptyStack }));
	const mrById = new Map(annotated.map((mr) => [mr.id, mr]));
	const sourceBuckets = new Map();

	for (const mr of annotated) {
		const key = sourceBranchKey(mr);
		if (!key) continue;
		if (!sourceBuckets.has(key)) sourceBuckets.set(key, []);
		sourceBuckets.get(key).push(mr);
	}

	const sourceMrByBranch = new Map();
	for (const [key, bucket] of sourceBuckets.entries()) {
		if (bucket.length === 1 && canBeStackParent(bucket[0])) {
			sourceMrByBranch.set(key, bucket[0]);
		}
	}

	// An MR's stack parent is the MR whose source branch it targets, falling
	// back to its first explicit GitLab dependency ("blocked by"). Any other
	// open dependencies are listed alongside the chain as alsoBlockedBy.
	const parentById = new Map();
	for (const mr of annotated) {
		const parent = sourceMrByBranch.get(targetBranchKey(mr));
		if (parent && parent.id !== mr.id) {
			parentById.set(mr.id, parent.id);
			continue;
		}
		const blocker = explicitBlockerIds(mr).find(
			(id) => id !== mr.id && mrById.has(id),
		);
		if (blocker) parentById.set(mr.id, blocker);
	}

	for (const cycleId of findCycleIds(mrById.keys(), parentById)) {
		parentById.delete(cycleId);
	}

	const childrenById = new Map(annotated.map((mr) => [mr.id, []]));
	for (const [childId, parentId] of parentById.entries()) {
		childrenById.get(parentId)?.push(childId);
	}
	for (const childIds of childrenById.values()) {
		childIds.sort((a, b) => byCreatedThenIid(mrById.get(a), mrById.get(b)));
	}

	const roots = annotated
		.filter((mr) => !parentById.has(mr.id))
		.sort(byCreatedThenIid);
	const visited = new Set();
	const components = [];

	const visit = (id, depth, component) => {
		if (visited.has(id)) return;
		visited.add(id);
		component.push({ id, depth });
		for (const childId of childrenById.get(id) || []) {
			visit(childId, depth + 1, component);
		}
	};

	for (const root of roots) {
		const component = [];
		visit(root.id, 0, component);
		if (component.length > 0) components.push(component);
	}

	for (const mr of annotated) {
		if (!visited.has(mr.id)) {
			const component = [];
			visit(mr.id, 0, component);
			if (component.length > 0) components.push(component);
		}
	}

	for (const component of components) {
		if (component.length < 2) continue;

		const stackId = component[0].id;
		const depthById = new Map(component.map((item) => [item.id, item.depth]));
		const reviewStart = summarize(mrById.get(stackId), 0);

		for (const { id, depth } of component) {
			const mr = mrById.get(id);
			const blockedById = parentById.get(id);
			const blockedBy = blockedById ? mrById.get(blockedById) : null;
			const childIds = childrenById.get(id) || [];
			const ancestorIds = [];
			let ancestorId = blockedById;
			while (ancestorId) {
				ancestorIds.unshift(ancestorId);
				ancestorId = parentById.get(ancestorId);
			}
			const chainIds = [
				...ancestorIds,
				id,
				...longestDescendantPath(id, childrenById, mrById),
			];
			const members = chainIds.map((chainId) =>
				summarize(mrById.get(chainId), depthById.get(chainId) || 0),
			);

			mr.stack = {
				isStacked: true,
				stackId,
				stackSize: chainIds.length,
				stackPosition: depth + 1,
				stackDepth: depth,
				isStackStart: !blockedById,
				isBlockedByStack: Boolean(blockedById),
				blockedBy: blockedBy
					? summarize(blockedBy, depthById.get(blockedById) || 0)
					: null,
				blocks: childIds.map((childId) =>
					summarize(mrById.get(childId), depthById.get(childId) || depth + 1),
				),
				alsoBlockedBy: explicitBlockerIds(mr)
					.filter(
						(blockerId) =>
							blockerId !== blockedById &&
							blockerId !== id &&
							mrById.has(blockerId),
					)
					.map((blockerId) => summarize(mrById.get(blockerId))),
				hiddenBlockers: mr.hiddenBlockingCount || 0,
				reviewStart,
				members,
			};
		}
	}

	return annotated;
};

/**
 * Stack parents that are not among `mrs` but are needed to complete their
 * chains: explicit dependencies by id, and branch targets that no known MR
 * sources (looked up by project + source branch). Long-lived targets such as
 * main are skipped, since nothing is stacked beneath them.
 */
export const findMissingStackParents = (mrs) => {
	const knownIds = new Set(mrs.map((mr) => mr.id));
	const knownSources = new Set(mrs.map(sourceBranchKey).filter(Boolean));
	const blockerIds = new Set();
	const branchesByProject = new Map();

	for (const mr of mrs) {
		for (const id of explicitBlockerIds(mr)) {
			if (!knownIds.has(id)) blockerIds.add(id);
		}

		const target = targetBranchKey(mr);
		if (!target || isLongLivedBranch(mr.targetBranch)) continue;
		if (knownSources.has(target)) continue;
		const projectPath = targetProjectPath(mr);
		if (!branchesByProject.has(projectPath)) {
			branchesByProject.set(projectPath, new Set());
		}
		branchesByProject.get(projectPath).add(mr.targetBranch);
	}

	return {
		blockerIds: [...blockerIds],
		branchLookups: [...branchesByProject].map(([projectPath, branches]) => ({
			projectPath,
			branches: [...branches],
		})),
	};
};

export const sortStackedMergeRequests = (mrs, compareItems) => {
	if (!mrs?.length) return [];

	const groupsByKey = new Map();
	for (const [index, mr] of mrs.entries()) {
		const key = mr.stack?.isStacked && mr.stack.stackId ? mr.stack.stackId : mr;
		if (!groupsByKey.has(key)) groupsByKey.set(key, []);
		groupsByKey.get(key).push({ mr, index });
	}

	const compareEntries = (a, b) =>
		compareItems(a.mr, b.mr) || a.index - b.index;
	const orderGroup = (entries) => {
		if (entries.length === 1) return entries;

		const entryById = new Map(entries.map((entry) => [entry.mr.id, entry]));
		const childIds = new Set();
		for (const entry of entries) {
			for (const child of entry.mr.stack?.blocks || []) {
				if (entryById.has(child.id)) childIds.add(child.id);
			}
		}

		const byDepthThenItem = (a, b) =>
			(a.mr.stack?.stackDepth || 0) - (b.mr.stack?.stackDepth || 0) ||
			compareEntries(a, b);
		const roots = entries
			.filter((entry) => !childIds.has(entry.mr.id))
			.sort(byDepthThenItem);
		const ordered = [];
		const visited = new Set();
		const visit = (entry) => {
			if (!entry || visited.has(entry.mr.id)) return;
			visited.add(entry.mr.id);
			ordered.push(entry);
			for (const child of entry.mr.stack?.blocks || []) {
				visit(entryById.get(child.id));
			}
		};

		for (const root of roots) visit(root);
		for (const entry of [...entries].sort(byDepthThenItem)) visit(entry);
		return ordered;
	};

	const groups = [...groupsByKey.values()].map(orderGroup);
	groups.sort((a, b) => compareEntries(a[0], b[0]));
	return groups.flatMap((group) => group.map(({ mr }) => mr));
};
